// Single source of truth for per-page SEO metadata.
// Consumed BOTH at build time (scripts/materialize-routes.js stamps these into
// each route's served HTML so crawlers get real per-page title/description/
// canonical without running JS) AND client-side (usePageMeta, for SPA nav).
// CommonJS so the Node build script can require it and React can import it.
//
// North Shore, Auckland is the primary service area — titles/descriptions lead
// with locksmith intent + North Shore / Auckland geography. Phone woven into
// descriptions for click-to-call in the SERP snippet.

const { serviceAreas } = require('../content/serviceAreas');
const { posts } = require('../content/blog');
const { categories, products, productUrl } = require('../content/products');
const shopContent = require('../content/shopContent');

const SITE_ORIGIN = 'https://www.shoredoors.co.nz';
const PHONE = '09 972 4640';

const routeMeta = {
  '/': {
    title: 'Locksmith North Shore Auckland | Shore Doors Locksmiths',
    description:
      `Mobile locksmiths for Auckland's North Shore — 24/7 lockouts, rekeying, key cutting, lock repairs and commercial door hardware. Fixed quotes, 12-month guarantee. Call ${PHONE}.`,
  },
  '/emergency-locksmith': {
    title: '24/7 Emergency Locksmith North Shore Auckland | Shore Doors',
    description:
      `Locked out on the North Shore? 24/7 mobile emergency locksmith for homes and businesses, weekends and public holidays included — fixed quote before we start. Call ${PHONE}.`,
  },
  '/residential': {
    title: 'Residential Locksmith North Shore | Shore Doors Locksmiths',
    description:
      `North Shore residential locksmiths — lockouts, rekeying, deadbolts, ranchslider locks, key cutting and home security upgrades. Mobile service from Greenhithe. Call ${PHONE}.`,
  },
  '/residential/lock-rekeying': {
    title: 'Lock Rekeying North Shore Auckland | Shore Doors',
    description:
      `Rekey your locks on the North Shore after moving house, lost keys or a tenancy change — old keys stop working. From $60 + GST per lock, mobile service. Call ${PHONE}.`,
  },
  '/residential/lock-repair-replacement': {
    title: 'Lock Repair, Replacement & Lock Changes North Shore | Shore Doors',
    description:
      `Sticking, worn or broken locks repaired or replaced, and every lock changed after a move or break-in, across Auckland's North Shore. 12-month guarantee. Call ${PHONE}.`,
  },
  '/residential/deadbolt-installation': {
    title: 'Deadbolt Installation & Replacement North Shore | Shore Doors',
    description:
      `Deadbolts (deadlocks) supplied, fitted and replaced across Auckland's North Shore — keyed alike to your other doors, strike set properly. Call ${PHONE}.`,
  },
  '/residential/sliding-door-locks': {
    title: 'Ranchslider Lock Repair & Replacement North Shore | Shore Doors',
    description:
      `Ranchslider and sliding door locks repaired and replaced on the North Shore — sticking hooks, worn cylinders, dropped rollers and keyless options. Call ${PHONE}.`,
  },
  '/residential/door-handle-repair': {
    title: 'Door Handle Repair & Replacement North Shore | Shore Doors',
    description:
      `Loose, drooping or broken door handles repaired or replaced across Auckland's North Shore — levers, knobs, entrance sets and sliding door handles. Call ${PHONE}.`,
  },
  '/residential/broken-key-extraction': {
    title: 'Broken Key Extraction North Shore | Shore Doors Locksmiths',
    description:
      `Key snapped in the lock or stuck fast? Broken key extraction across Auckland's North Shore, usually without damaging the lock, and a new key cut on the spot. Call ${PHONE}.`,
  },
  '/residential/smart-lock-installation': {
    title: 'Smart Lock & Digital Lock Installation North Shore | Shore Doors',
    description:
      `Smart and digital door locks installed and set up on the North Shore — keypad, fingerprint and app locks for timber and aluminium doors. Call ${PHONE}.`,
  },
  '/residential/key-cutting': {
    title: 'Mobile Key Cutting North Shore Auckland | Shore Doors',
    description:
      `Keys cut at your place on the North Shore from our mobile van — house, window and padlock keys, keyed-alike options. Call ${PHONE}.`,
  },
  '/residential/home-security-upgrades': {
    title: 'Home Security Upgrades North Shore | Shore Doors',
    description:
      `Deadbolts and deadlocks, window locks, sliding-door locks, smart locks and safes fitted across the North Shore. Insurance-friendly home security done properly. Call ${PHONE}.`,
  },
  '/commercial': {
    title: 'Commercial Locksmith Auckland | Shore Doors Locksmiths',
    description:
      `Commercial locksmiths and door hardware across Auckland — the install team security companies trust. Master keying, access control, maintenance. Request a quote.`,
  },
  '/commercial/commercial-locksmith': {
    title: 'Commercial Locksmith North Shore & Auckland | Shore Doors',
    description:
      `Business lockouts, rekeying after staff changes, restricted keys and shopfront hardware across Auckland — from the installers the pros use. Request a quote.`,
  },
  '/commercial/door-hardware-installation': {
    title: 'Commercial Door Hardware Installation Auckland | Shore Doors',
    description:
      `Mortice locks, panic hardware, door closers and fire-door hardware installed to NZ Building Code across Auckland. Trusted commercial installers. Request a quote.`,
  },
  '/commercial/door-closers': {
    title: 'Door Closer Installation & Repair Auckland | Shore Doors',
    description:
      `Door closers installed, adjusted and replaced for Auckland businesses and homes — fire doors, gates and busy entries set to close and latch properly. Request a quote.`,
  },
  '/commercial/access-control-installation': {
    title: 'Access Control Installation Auckland | Shore Doors',
    description:
      `Maglocks, electric strikes, drop bolts and wireless locks installed to your integrator's spec across Auckland — cabling, commissioning, handover. Request a quote.`,
  },
  '/commercial/door-automation': {
    title: 'Automatic Door Openers Auckland — AHS A200 | Shore Doors',
    description:
      `Auckland's only certified AHS A200 door actuator installer — automatic door opening, installation, repair and servicing. Accessibility and ACC work for disabled customers. Request a quote.`,
  },
  '/commercial/master-key-systems': {
    title: 'Master Key Systems Auckland | Shore Doors Locksmiths',
    description:
      `Master key and restricted key systems designed, installed and managed for Auckland businesses, schools and body corporates — proper key control. Request a quote.`,
  },
  '/commercial/maintenance-iqp': {
    title: 'IQP Support & Door Maintenance Auckland | Shore Doors',
    description:
      `Door maintenance checks, fire-door and automatic door repairs, and the remedial work IQP inspections turn up — alongside your IQP and access control provider. Request a quote.`,
  },
  '/partners': {
    title: 'Installation Partner for Security Companies | Shore Doors',
    description:
      `Shore Doors is the installation delivery partner Auckland's access control companies and builders rely on — hardware installs, cabling, commissioning, handover.`,
  },
  '/industries': {
    title: 'Industries We Serve | Shore Doors Locksmiths Auckland',
    description:
      `Door hardware and access control installation for healthcare, education, government, data centres and corporate offices across Auckland. Request a quote.`,
  },
  '/projects': {
    title: 'Completed Projects | Shore Doors Locksmiths Auckland',
    description:
      `Real installations across Auckland — North Shore Hospital, BNZ Queen Street, Fujifilm House of Photography and more. Door hardware and access control.`,
  },
  '/reviews': {
    title: 'Reviews & Testimonials | Shore Doors Locksmiths',
    description:
      `What Auckland's security companies and clients say about Shore Doors Locksmiths — reliable installs, quality workmanship and easy to deal with.`,
  },
  '/about': {
    title: 'About | North Shore Locksmiths | Shore Doors Locksmiths',
    description:
      `From trusted install partner of Auckland's security companies to full-service North Shore locksmiths — our story, team and standards. Call ${PHONE}.`,
  },
  '/contact': {
    title: 'Contact | Locksmith North Shore Auckland | Shore Doors',
    description:
      `Call Shore Doors Locksmiths on ${PHONE} or send an enquiry — North Shore residential and Auckland commercial locksmith and door hardware services.`,
  },
  '/pricing': {
    title: 'Locksmith Prices North Shore Auckland | Shore Doors Locksmiths',
    description:
      `Clear locksmith pricing for the North Shore — $120 + GST call-out including the first 15 minutes on site, then $30 + GST per 15 minutes. After hours (24/7) $225 + GST. Fixed quote before any work starts. Call ${PHONE}.`,
  },
  '/service-areas': {
    title: 'North Shore Locksmith — Suburbs We Cover | Shore Doors',
    description:
      `North Shore locksmith based in Greenhithe, covering Takapuna, Albany, Glenfield, Devonport, Browns Bay, Milford and every suburb between. Fixed quotes. Call ${PHONE}.`,
  },
  '/blog': {
    title: 'Locksmith Tips & Guides | Shore Doors Locksmiths',
    description:
      `Locksmith advice for Auckland's North Shore — what a locksmith costs, what to do when locked out, rekey vs replace, and more from Shore Doors Locksmiths.`,
  },
};

// Generate a metadata entry per suburb service-area page (single source of
// truth — the build script and client hook both read these).
// Descriptions lead with the phone (so it survives truncation in results) and carry each suburb's
// own first job, so no two suburb pages share a description.
for (const area of serviceAreas) {
  const firstJob = area.jobs && area.jobs.length ? `${area.jobs[0]}, plus lockouts` : 'Lockouts';
  routeMeta[`/service-areas/${area.slug}`] = {
    title: `Locksmith ${area.name} | Shore Doors Locksmiths`,
    description: area.isBase
      ? `Locksmith based in ${area.name} — call ${PHONE}. ${firstJob}, rekeys and lock repairs across the North Shore. Fixed quotes, 12-month guarantee.`
      : `Locksmith in ${area.name}, North Shore — call ${PHONE}. ${firstJob}, rekeys and lock repairs. Fixed quotes, 12-month guarantee.`,
  };
}

// Blog posts — per-post meta from the post's own metaTitle/metaDescription.
for (const post of posts) {
  routeMeta[`/blog/${post.slug}`] = {
    title: post.metaTitle || `${post.title} | Shore Doors Locksmiths`,
    description: post.metaDescription || post.excerpt,
  };
}

// Shop routes (see content/products.js + content/shopContent.js). noindex marks
// routes that are materialised (Render has no SPA rewrite) but never indexed or
// listed in the sitemap. materialize-routes.js also forces noindex on every
// shop route while catalogueMeta.status is 'draft'.
routeMeta['/shop'] = { title: shopContent.hub.metaTitle, description: shopContent.hub.metaDescription };
routeMeta['/shop/e-lok'] = { title: shopContent.elokHub.metaTitle, description: shopContent.elokHub.metaDescription };
routeMeta['/shop/aqara'] = { title: shopContent.aqaraHub.metaTitle, description: shopContent.aqaraHub.metaDescription };
routeMeta['/shop/find-my-lock'] = { title: shopContent.findMyLock.metaTitle, description: shopContent.findMyLock.metaDescription };
for (const g of require('../content/shopGuides').guides) {
  routeMeta[`/shop/guides/${g.slug}`] = { title: g.metaTitle, description: g.metaDescription };
}
routeMeta['/shop/will-it-fit'] = { title: shopContent.willItFit.metaTitle, description: shopContent.willItFit.metaDescription };
{
  const { measureGuide } = require('../content/measureGuide');
  routeMeta['/shop/how-to-measure'] = { title: measureGuide.metaTitle, description: measureGuide.metaDescription };
}
routeMeta['/shop/installation'] = { title: shopContent.installation.metaTitle, description: shopContent.installation.metaDescription };
routeMeta['/shop/trade'] = { title: shopContent.trade.metaTitle, description: shopContent.trade.metaDescription };
for (const key of Object.keys(shopContent.policies)) {
  routeMeta[`/shop/${key}`] = { title: shopContent.policies[key].metaTitle, description: shopContent.policies[key].metaDescription };
}
for (const cat of categories) {
  routeMeta[`/shop/${cat.slug}`] = { title: cat.metaTitle, description: cat.metaDescription };
}
for (const p of products) {
  routeMeta[productUrl(p)] = {
    title: (p.seo && p.seo.title) || `${p.name} NZ | Shore Doors Locksmiths`,
    description:
      (p.seo && p.seo.description) ||
      `${p.shortName}: ${p.fit.notes || p.name}. ${p.priceIncGst} incl. GST plus courier NZ-wide${p.install && p.install.eligible ? ', or supplied and fitted in Auckland at a fixed price' : ''}. Manufacturer's warranty. Shore Doors Locksmiths.`,
  };
}
routeMeta['/shop/cart'] = { title: 'Your cart | Shore Doors Locksmiths', description: 'Review your order, add installation and pay.', noindex: true };
routeMeta['/shop/order/success'] = { title: 'Order received | Shore Doors Locksmiths', description: 'Thanks — your order is confirmed.', noindex: true };
routeMeta['/shop/order/cancelled'] = { title: 'Payment cancelled | Shore Doors Locksmiths', description: 'Your payment was cancelled; your cart is saved.', noindex: true };
// Photos come after payment (the success page and the confirmation email link here) — and,
// only when the chooser cannot pick a lock, before one. Neither page is indexed or linked.
routeMeta['/shop/order/photos'] = { title: 'Photos of your door | Shore Doors Locksmiths', description: 'Send the photos of the door for your order.', noindex: true };
routeMeta['/shop/find-my-lock/photos'] = { title: 'Send photos of your door | Shore Doors Locksmiths', description: 'A locksmith picks the lock from your photos.', noindex: true };
routeMeta['/admin/orders'] = { title: 'Orders | Shore Doors', description: 'Order administration.', noindex: true };

module.exports = { routeMeta, SITE_ORIGIN, PHONE };
